import axios from "axios";
import { message } from "antd";
import { revisarLineaDeVenta, descuentoMinimoAplicable, resolverReglaPrecio } from "./reglasPrecio";

/**
 * Maquinaria de reglas de precio para una pantalla de captura de documentos.
 *
 * Las seis pantallas (Venta, Cotizacion, Pedido, Embarque — agregar y editar)
 * comparten los mismos nombres de metodo y la misma forma del formulario, asi que
 * en vez de copiar ~120 lineas en cada una se les entrega este helper.
 *
 *   reglas = crearReglasPrecio(this, { modulo: "PEDIDO" })
 *
 * Lee y escribe `componente.state.contextoReglas` y `componente.state.fueraDeRango`,
 * y los valores del formulario por `componente.formRef`. La pantalla solo tiene que
 * declarar esas dos llaves en su state y llamar a los metodos en los puntos de
 * enganche.
 *
 * @param componente          la instancia de la pantalla (se usa this.state, this.props, this.formRef)
 * @param opciones.modulo     "VENTA" | "COTIZACION" | "PEDIDO" | "EMBARQUE"
 * @param opciones.filaBloqueada  (index) => boolean — filas que no se tocan ni se marcan
 *                                (ej. esLineaVendida en EditarPedido)
 * @param opciones.recalcularTotales  (insumos) => void — el recalcularInsumosEnBatch de la
 *                                pantalla. Se llama cuando el helper precarga descuentos, para
 *                                que subtotales e impuestos queden al dia con un solo
 *                                setFieldsValue.
 * @param opciones.autoDescuento  () => boolean — apaga SOLO la precarga del descuento minimo,
 *                                dejando vivos los avisos por renglon y el candado al guardar.
 *                                Para pantallas donde el descuento no se puede capturar y
 *                                precargarlo dejaria los totales sin respaldo. Por omision, on.
 */
export const crearReglasPrecio = (
  componente,
  { modulo, filaBloqueada, recalcularTotales, autoDescuento } = {}
) => {
  const estaBloqueadaLaFila = (index) =>
    typeof filaBloqueada === "function" ? !!filaBloqueada(index) : false;

  const insumosDelForm = () => componente.formRef.current?.getFieldValue("insumos") || [];

  const insumoDelCatalogo = (insumoId) =>
    (componente.state.todasInsumos || []).find((i) => i._id === insumoId);

  // El costo unitario de una equivalencia se DIVIDE entre su factor.
  const costoDeCatalogo = (insumoCatalogo) =>
    insumoCatalogo.noPrincipal
      ? insumoCatalogo.costo / (insumoCatalogo.equivalencia || 1)
      : insumoCatalogo.costo;

  const helper = {
    /**
     * Carga las reglas aplicables al cliente y la lista actuales.
     *
     * Llegan en crudo: la resolucion final depende de la familia y la marca de
     * cada insumo, asi que se apilan renglon por renglon.
     */
    consultarContexto: async (clienteId, precioId) => {
      const form = componente.formRef.current;
      const cliente = clienteId ?? form?.getFieldValue("clienteId");
      const precio = precioId ?? form?.getFieldValue("precioId");

      if (!precio) {
        componente.setState({ contextoReglas: null, fueraDeRango: {} });
        return;
      }

      try {
        const res = await axios.post(
          "reglasPrecio/reglasParaDocumento",
          { empresaId: componente.props.empresaId, clienteId: cliente, precioId: precio },
          { headers: componente.props.headersToken }
        );
        if (res.data.success === 2) {
          componente.setState({ contextoReglas: res.data.contexto }, () => {
            // El descuento minimo se aplica AQUI y no en quien cambia la lista,
            // porque esta carga es asincrona: si se hiciera afuera se usarian las
            // reglas viejas. Asi el orden de las llamadas deja de importar.
            helper.aplicarDescuentoMinimoYRecalcular();
            helper.revisarTodas();
          });
        }
      } catch (error) {
        // Sin contexto el candado del backend sigue funcionando; aqui solo se
        // pierde el aviso en vivo, asi que no vale la pena molestar al usuario.
        console.error("No se pudieron cargar las reglas de precio:", error);
      }
    },

    /**
     * Revisa un renglon contra las reglas. Regresa la violacion o null.
     * NO bloquea la captura: el vendedor tiene que poder teclear el valor para
     * poder pedir autorizacion sobre el.
     */
    revisarFila: (index, insumosOverride) => {
      const contexto = componente.state.contextoReglas;
      if (!contexto) return null;
      if (estaBloqueadaLaFila(index)) return null;

      const insumos = insumosOverride || insumosDelForm();
      const fila = insumos[index];
      if (!fila || !fila.insumoId) return null;

      const insumoCatalogo = insumoDelCatalogo(fila.insumoId);
      if (!insumoCatalogo) return null;

      return revisarLineaDeVenta({
        contexto,
        insumo: insumoCatalogo,
        precioUnitario: fila.precioUnitario,
        cantidad: fila.cantidad,
        descuento: fila.descuento,
        descuentoCantidad: fila.descuentoCantidad,
        costo: costoDeCatalogo(insumoCatalogo),
      });
    },

    actualizarFila: (index) => {
      if (!componente.state.contextoReglas) return;
      const violacion = helper.revisarFila(index);
      componente.setState((prev) => {
        const fueraDeRango = { ...prev.fueraDeRango };
        if (violacion) fueraDeRango[index] = violacion;
        else delete fueraDeRango[index];
        return { fueraDeRango };
      });
    },

    /**
     * Revisa todos los renglones de golpe. Se usa al cargar el contexto y
     * despues de mover o eliminar filas, donde los indices se recorren.
     */
    revisarTodas: () => {
      if (!componente.state.contextoReglas) {
        if (Object.keys(componente.state.fueraDeRango || {}).length) {
          componente.setState({ fueraDeRango: {} });
        }
        return {};
      }
      const insumos = insumosDelForm();
      const fueraDeRango = {};
      insumos.forEach((fila, index) => {
        const violacion = helper.revisarFila(index, insumos);
        if (violacion) fueraDeRango[index] = violacion;
      });
      componente.setState({ fueraDeRango });
      return fueraDeRango;
    },

    /**
     * Primera violacion que todavia no tiene autorizacion.
     *
     * El perdon es por campo y por documento, igual que en el backend: una
     * autorizacion de descuento cubre los demas renglones del mismo documento.
     */
    primeraSinAutorizar: () => {
      const fueraDeRango = helper.revisarTodas();

      const camposAutorizados = new Set(
        Object.entries(componente.state.autorizacionesActivas || {})
          .filter(([, datos]) => datos?.autorizacionId)
          .map(([clave]) => clave.split(":")[0])
      );

      for (const [index, violacion] of Object.entries(fueraDeRango)) {
        if (camposAutorizados.has(violacion.campo)) continue;
        return { ...violacion, index: Number(index) };
      }
      return null;
    },

    /**
     * Descuento minimo que le toca a un renglon, ya acotado por el margen minimo
     * y por el tope de descuento. Regresa undefined si no hay nada que precargar.
     *
     * Solo aplica en modo porcentaje: el descuento en cantidad y el porcentaje
     * son excluyentes en la captura, y precargar un porcentaje sobre una linea
     * que el vendedor puso en pesos le borraria lo capturado.
     */
    descuentoMinimoParaFila: (index, filaOverride) => {
      const contexto = componente.state.contextoReglas;
      if (!contexto || !contexto.listaBase) return undefined;
      if (estaBloqueadaLaFila(index)) return undefined;
      if (componente.state.tiposDescuento?.[index] === "cantidad") return undefined;

      const fila = filaOverride || insumosDelForm()[index];
      if (!fila || !fila.insumoId) return undefined;

      const insumoCatalogo = insumoDelCatalogo(fila.insumoId);
      if (!insumoCatalogo) return undefined;

      const regla = resolverReglaPrecio({
        listaBase: contexto.listaBase,
        reglas: contexto.reglas,
        insumo: insumoCatalogo,
        clienteId: contexto.clienteId,
        tipoClienteId: contexto.tipoClienteId,
        priorizarTipoCliente: contexto.priorizarTipoCliente,
      });

      return descuentoMinimoAplicable({
        regla,
        precioUnitario: fila.precioUnitario,
        cantidad: fila.cantidad,
        costo: costoDeCatalogo(insumoCatalogo),
        calcularPreciosSobre: contexto.calcularPreciosSobre,
      });
    },

    /**
     * Escribe el descuento minimo en el renglon del arreglo que se le pase, si
     * procede. Muta `fila` a proposito: los handlers de las pantallas arman el
     * arreglo completo y hacen un solo setFieldsValue al final.
     *
     * Solo precarga sobre un descuento en 0 o vacio: si el vendedor ya lo movio
     * a mano, se respeta.
     */
    aplicarDescuentoMinimoAFila: (index, fila) => {
      if (!fila) return false;
      const actual = Number(fila.descuento) || 0;
      if (actual !== 0) return false;

      const minimo = helper.descuentoMinimoParaFila(index, fila);
      if (minimo === undefined || minimo <= 0) return false;

      fila.descuento = minimo;
      // Renglon con desglose (complemento Escuela): el minimo va como adicional.
      // Aqui la base vale 0 (el efectivo era 0), asi que la cascada da `minimo`.
      if ("descuentoBase" in fila || "descuentoAdicional" in fila) {
        fila.descuentoAdicional = minimo;
      }
      return true;
    },

    /**
     * Precarga el descuento minimo en todas las filas que sigan en 0 y, si movio
     * alguna, manda recalcular totales y avisa una sola vez.
     *
     * Se llama al terminar de cargar el contexto (cambio de cliente, de lista o
     * de contado) y no cuesta nada cuando la lista no tiene descuento minimo.
     */
    aplicarDescuentoMinimoYRecalcular: () => {
      const contexto = componente.state.contextoReglas;
      if (!contexto || !contexto.listaBase) return 0;
      if (typeof autoDescuento === "function" && !autoDescuento()) return 0;

      // Se trabaja sobre una copia: mutar el arreglo del form sin avisar deja la
      // vista desincronizada del store de antd.
      const insumos = insumosDelForm().map((fila) => ({ ...fila }));
      let cambiadas = 0;
      insumos.forEach((fila, index) => {
        if (helper.aplicarDescuentoMinimoAFila(index, fila)) cambiadas++;
      });

      if (!cambiadas) return 0;

      if (typeof recalcularTotales === "function") {
        recalcularTotales(insumos);
      } else {
        componente.formRef.current?.setFieldsValue({ insumos });
      }

      message.info(
        cambiadas === 1
          ? "Se aplico el descuento minimo de la lista de precios a un insumo"
          : `Se aplico el descuento minimo de la lista de precios a ${cambiadas} insumos`
      );
      return cambiadas;
    },

    modulo,
  };

  return helper;
};

export default crearReglasPrecio;
